import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, BookOpen, Share2, Users, MousePointer2, Move, PenLine, CircleDot, Play } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CreateTeamDialog } from "@/pages/Teams";
import { PlayCard } from "@/components/PlayCard";

const STEPS = [
  { icon: Move, t: "Line them up", d: "Open a play and use Move Player to drag markers into your formation." },
  { icon: PenLine, t: "Draw the routes", d: "Pick Draw Route, tap a player, then tap the field to add points." },
  { icon: CircleDot, t: "Move the ball", d: "Use Ball Action: tap who has the ball, then who gets it." },
  { icon: Play, t: "Save & play it", d: "Press Save, then Learn to animate it and show each kid their job." },
];

export default function Dashboard() {
  const { user, teams, activeTeam, setActiveTeam } = useAuth();
  const [plays, setPlays] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { if (activeTeam) api.get(`/teams/${activeTeam.id}/plays`).then((r) => setPlays(r.data)).catch(() => setPlays([])); }, [activeTeam]);

  return (
    <div className="space-y-8">
      <section className="relative overflow-hidden rounded-3xl turf-bg p-6 sm:p-10 tpc-rise" data-testid="dashboard-hero">
        <div className="absolute inset-0 bg-gradient-to-r from-[#070D18]/90 via-[#070D18]/60 to-transparent" />
        <div className="relative max-w-xl">
          <p className="label text-slate-200">Welcome{user?.name ? `, ${user.name}` : ", Coach"}</p>
          <h1 className="h1">{activeTeam ? activeTeam.name : "Let's build your playbook"}</h1>
          {activeTeam && <p className="text-slate-200 mt-2" data-testid="dashboard-team-meta">{[activeTeam.age_group, activeTeam.format === "flag" ? "Flag" : "Tackle", `${activeTeam.player_count} per side`, `${activeTeam.play_count ?? plays.length} plays`].filter(Boolean).join(" · ")}</p>}
          <div className="flex flex-wrap gap-2 mt-6">
            {activeTeam ? (
              <>
                <Link to={`/teams/${activeTeam.id}/playbook`} className="btn btn-primary text-base" data-testid="dash-open-playbook"><BookOpen size={18} />Open playbook</Link>
                <Link to={`/teams/${activeTeam.id}/settings`} className="btn btn-ghost" data-testid="dash-share"><Share2 size={18} />Share with team</Link>
              </>
            ) : <button className="btn btn-primary text-base" onClick={() => setOpen(true)} data-testid="dash-create-team"><Plus size={18} />Create your first team</button>}
          </div>
        </div>
      </section>

      {teams.length > 1 && (
        <section className="flex flex-wrap items-center gap-2" data-testid="dash-team-switcher">
          <Users size={18} className="text-slate-400" />
          {teams.map((t) => <button key={t.id} className={`chip ${activeTeam?.id === t.id ? "chip-on" : ""}`} onClick={() => setActiveTeam(t.id)} data-testid={`dash-team-${t.id}`}>{t.name}</button>)}
        </section>
      )}

      <section>
        <h2 className="h2 mb-4 flex items-center gap-2"><MousePointer2 size={18} className="text-[#FF8A1F]" />Your first play in 4 steps</h2>
        <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="first-play-guide">
          {STEPS.map((s, i) => (
            <li key={s.t} className="card p-4 tpc-rise" style={{ animationDelay: `${i * 70}ms` }}>
              <div className="flex items-center gap-2"><span className="font-display text-3xl font-extrabold text-[#FF8A1F]">{i + 1}</span><s.icon size={20} /></div>
              <p className="font-bold mt-2">{s.t}</p>
              <p className="text-sm text-slate-400 mt-1">{s.d}</p>
            </li>
          ))}
        </ol>
      </section>

      {activeTeam && plays.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-4"><h2 className="h2">Recent plays</h2><Link to={`/teams/${activeTeam.id}/playbook`} className="text-sm font-bold text-[#FF8A1F]" data-testid="dash-see-all">See all</Link></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {plays.slice(0, 3).map((p) => <PlayCard key={p.id} play={p} team={activeTeam} learnTo={`/play/${p.id}/learn`} editTo={`/play/${p.id}/edit`} />)}
          </div>
        </section>
      )}
      <CreateTeamDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
